import { CHART_CONFIG, TICK_FORMATS } from "@/config/chart";
import { STATS_CONFIG } from "@/config/series";
import { Axis } from "@/types/chart";
import { SeriesPoint } from "@/types/common";
import { COLOR_PALETTE } from "@/colors";
import { getWholeFromPrefix } from "@/utils/conversion";
import * as d3 from "d3";
import _ from "lodash";
import { StatType } from "@/types/stats";

export function getConfig(chartID: string, axis: Axis) {
  return CHART_CONFIG[chartID].axes[axis];
}

export function getAxisByMetric(chartID: string, metric: string): Axis {
  return _.includes(getConfig(chartID, Axis.RIGHT).metrics, metric) ? Axis.RIGHT : Axis.LEFT;
}

export function getMetrics(chartID: string): string[] {
  const metricsLeft = getConfig(chartID, Axis.LEFT).metrics;
  const metricsRight = getConfig(chartID, Axis.RIGHT).metrics;
  return [...metricsLeft, ...metricsRight];
}

export function getChartIDs(): string[] {
  return Object.keys(CHART_CONFIG);
}

export function getChartIDsFor(statType: StatType): string[] {
  return getChartIDs().filter(chartID => CHART_CONFIG[chartID].statType === statType);
}

export function getAllProcessedStatsNames(): string[] {
  const allMetrics: any[] = [];

  getChartIDsFor(StatType.PROCESSED).forEach(chartID => {
    const metrics = getMetrics(chartID);
    if (metrics.length > 0) {
      // Only get unique stats
      metrics.filter(metric => {
        if (!_.includes(allMetrics, metric)) {
          allMetrics.push(metric);
        }
      });
    }
  });

  return allMetrics;
}

function findMaxByMetric(values: number[][], columnIndex: number): number {
  const maxRow = _.maxBy(values, (x: any[]) => x[columnIndex]);
  if (maxRow === undefined) {
    return 0;
  }
  // if max value is zero, set a default
  // so the chart shows an arbitrary y-axis scale
  // so the plots bottom align, and don't vertical center align
  if (maxRow[columnIndex] === 0) {
    return 1;
  } else {
    return maxRow[columnIndex];
  }
}

function addLeadingZero(n: number) {
  return n <= 9 ? "0" + n : n;
}

function getTooltipFormat(chartID: string, metric: string, d: number): string {
  const axis = getAxisByMetric(chartID, metric);

  if (getConfig(chartID, axis).inputPrefix) {
    d = getWholeFromPrefix(d, getConfig(chartID, axis).inputPrefix);
  }

  const format = getConfig(chartID, axis).format;
  const unit = getConfig(chartID, axis).unit;

  if (format === TICK_FORMATS.si) {
    // SI prefixed, round to two points after decimal
    return d3.formatPrefix(".2f", d)(d) + unit;
  } else if (format === TICK_FORMATS.percentage) {
    // Whole percent
    return d3.format(format)(d);
  } else {
    // Round to two points after decimal, add thousands separator
    return d3.format(",.2f")(d) + unit;
  }
}

export function yAxisIs(chartID: string, axis: Axis, metric: string): boolean {
  if (axis === Axis.BOTH) {
    return true;
  } else {
    return _.includes(getConfig(chartID, axis).metrics, metric);
  }
}

export function getYMetrics(chartID: string, axis: Axis): string[] {
  return getConfig(chartID, axis).metrics;
}

export function formatIsPercentage(chartID: string, axis: Axis): boolean {
  return getConfig(chartID, axis).format === TICK_FORMATS.percentage;
}

export function getValuesIdxByMetric(metrics: string[], metric: string): number {
  return metrics.indexOf(metric);
}

export function isStacked(chartID: string): boolean {
  return CHART_CONFIG[chartID].stacked;
}

function findMaxSummedPoint(values: SeriesPoint[]): number {
  let max = 0;
  for (const point of values) {
    // slice to remove time
    const sum = point.slice(1).reduce((accumulator: number, currentVal: number) => accumulator + currentVal);

    if (sum > max) {
      max = sum;
    }
  }
  return max;
}

function findMaxVal(chartID: string, axis: Axis, metrics: string[], values: SeriesPoint[]) {
  const max = _.max(
    metrics.map((metric: string, idx: number) => {
      if (yAxisIs(chartID, axis, metric)) {
        return findMaxByMetric(values, idx + 1);
      }
    }),
  );

  if (max === undefined) {
    return 1;
  }
  return max;
}

export function findMaxForYAxis(chartID: string, axis: Axis, metrics: string[], values: SeriesPoint[]): number {
  // Axes showing percentages should always have max 100%
  if (formatIsPercentage(chartID, axis)) {
    return 1;
  }

  let max = 0;

  // if stacked
  if (axis == Axis.LEFT && isStacked(chartID)) {
    max = findMaxSummedPoint(values);
  } else {
    max = findMaxVal(chartID, axis, metrics, values);
  }

  // add 10% to max value to give breathing room
  // between chart top and max plot
  return max > 1 ? max * 1.1 : max;
}

export function getMetricLabel(metricID: string): string {
  return STATS_CONFIG[metricID];
}

export function hasAxisRight(chartID: string): boolean {
  return getYMetrics(chartID, Axis.RIGHT).length > 0;
}

// Prepare values for a percent based stacked area chart
export function stackValuesPercent(values: any[]): any[] {
  values.forEach(value => {
    // get total of values, so we know what 100% is
    // start at 1 to skip `time`
    let total = 0;
    for (let i = 1; i < value.length; i++) {
      total += value[i];
    }

    // do not divide by zero
    // no need to calculate as percent,
    // if total is 1 values are already fractions of 1
    if (total > 0 && total !== 1) {
      // set each value as percent of total
      // start at 1 to skip `time`
      for (let i = 1; i < value.length; i++) {
        value[i] = value[i] / total;
      }
    }
  });
  return values;
}

export function getYLabel(chartID: string, axis: Axis): string {
  return getConfig(chartID, axis).label;
}

export function getTickFormat(chartID: string, axis: Axis, d: any): string {
  if (getConfig(chartID, axis).inputPrefix) {
    d = getWholeFromPrefix(d, getConfig(chartID, axis).inputPrefix);
  }
  const format = getConfig(chartID, axis).format;
  const unit = getConfig(chartID, axis).unit;

  return d3.format(format)(d) + unit;
}

export function getTitle(chartID: string): string {
  return CHART_CONFIG[chartID].title;
}

export function tooltipTemplateTime(time: Date): string {
  const formattedTime =
    time.getFullYear() +
    "-" +
    addLeadingZero(time.getMonth() + 1) +
    "-" +
    addLeadingZero(time.getDate()) +
    " " +
    addLeadingZero(time.getHours()) +
    ":" +
    addLeadingZero(time.getMinutes()) +
    ":" +
    addLeadingZero(time.getSeconds());

  return `<div class="tooltip-time">${formattedTime}</div>`;
}

function tooltipTemplateHTML(label: string, color: string, value: string) {
  return `
    <div class="tooltip-metric-unit">
      <div class="tooltip-metric-color" style="background: ${color}" aria-hidden="true"></div>
      ${label}
    </div>
    ${value}`;
}

export function tooltipTemplateMetric(chartID: string, metricIdx: number, d: any, iClosest: number): string {
  const color = COLOR_PALETTE[metricIdx];
  const label = getMetricLabel(d.metric);
  const formattedValue = getTooltipFormat(chartID, d.metric, d.values[iClosest].value);
  return tooltipTemplateHTML(label, color, formattedValue);
}

export function tooltipTemplateStat(i: number, d: any, iClosest: number, format: string, unit: string): string {
  const color = COLOR_PALETTE[i];
  const label = d.metric;
  const value = d[iClosest].total_size;

  // Round to two points after decimal, add thousands separator
  let formattedValue = d3.format(",.2f")(value) + unit;
  if (format === TICK_FORMATS.si) {
    // SI prefixed, round to two points after decimal
    formattedValue = d3.formatPrefix(".2f", value)(value) + unit;
  } else if (format === TICK_FORMATS.percentage) {
    // Whole percent
    formattedValue = d3.format(format)(value);
  }

  return tooltipTemplateHTML(label, color, formattedValue);
}

// Position the tooltip so it never bleeds off the screen
export function tooltipX(tooltip: any, tooltipID: string) {
  const element = document.getElementById(tooltipID);
  const cursorX = d3.event.layerX;
  const space = 16; // corresponds to 1em/$space sass var

  if (element !== null) {
    const width = element.getBoundingClientRect().width;

    // Add extra space so tooltip position switches before touching window boundary
    const limit = d3.event.clientX + width + space * 2;

    // before the tooltip bleeds of the right side of the screen, reposition left of cursor
    if (limit > window.innerWidth) {
      tooltip.attr("class", "tooltip tooltip-left");
      return `${cursorX}px`;
    }
  }
  tooltip.attr("class", "tooltip");
  return `${cursorX + space * 2}px`; // default position right of cursor
}

export function legendTemplate(label: string, idx: number): string {
  const color = COLOR_PALETTE[idx];
  return `
    <div class="legend-item">
      <div class="legend-color" style="background: ${color}" aria-hidden="true"></div>
      ${label}
    </div>`;
}

export function mouseout(focus: any, tooltip: any, g: any) {
  focus.style("display", "none");
  tooltip.style("display", "none");
  g.selectAll(".circles circle").style("display", "none");
}

export function setYAxis(
  chartID: string,
  axis: Axis,
  metrics: string[],
  values: any,
  g: any,
  x: any,
  y: any,
  legend: any,
) {
  const legendList = legend.append("ul").attr("class", "legend-list");

  getYMetrics(chartID, axis).forEach((metric: string) => {
    // if the config metric is not found in the chart metrics don't add it to the chart.
    // e.g. when a per server chart for a MemCachier cache has a backend stat, do not show.
    // it should only be shown for an aggregate chart
    if (metrics.indexOf(metric) === -1) return;

    const idx = getValuesIdxByMetric(metrics, metric);

    // Add legend
    legendList.append("li").html(() => {
      const label = getMetricLabel(metric);
      return legendTemplate(label, idx);
    });

    if (!isStacked(chartID)) {
      // Add lines between plots
      const line = d3
        .line()
        .defined(function(d) {
          return d[idx + 1] !== null;
        })
        .x((d: any) => x(new Date(d[0])))
        .y((d: any) => y(d[idx + 1]))
        .curve(d3.curveMonotoneX);

      // Add plot lines for metric
      g.append("path")
        .datum(values)
        .attr("fill", "none")
        .attr("stroke", COLOR_PALETTE[idx])
        .attr("stroke-width", 2)
        .attr("d", line);
    }
  });
}
